import React, { useState } from "react";
import { 
  Search, Package, RefreshCw, CreditCard, User, 
  Truck, ShieldAlert, ChevronDown, ChevronUp, 
  Phone, Mail, MessageSquare 
} from "lucide-react";

const HelpCenter: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  // Toggle FAQ
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // FAQ Data
  const faqs = [
    {
      question: "How do I track my bulk order?",
      answer: "You can track your order by navigating to 'My Orders' section in your dashboard. Once the order is dispatched, a tracking ID will be updated which you can use on our logistics partner's website."
    },
    {
      question: "What is the return policy for damaged goods?",
      answer: "We have a 7-day return policy for damaged or defective items. Please raise a return request within 24 hours of delivery with photos of the damaged shipment."
    },
    {
      question: "How do I apply for a Credit Line (Udhaar)?",
      answer: "Registered retailers can apply for a credit line by uploading their GST certificate and 6-month bank statement in the 'Business Finance' section. Approval takes 24-48 hours."
    },
    {
      question: "Can I cancel my order after payment?",
      answer: "Orders can be cancelled within 4 hours of placement. Once the status changes to 'Processing' or 'Shipped', cancellation is not possible."
    },
    {
      question: "How do I contact the supplier directly?",
      answer: "To maintain security and trust, direct contact is not allowed initially. You can use the 'Message Seller' feature on the product page to ask technical queries."
    }
  ];

  return (
    <div className="font-sans text-gray-800 bg-gray-50 min-h-screen">
      
      {/* --- 1. HERO SEARCH SECTION --- */}
      <section className="bg-[#1e293b] py-20 text-center px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            How can we help you?
          </h1>
          <p className="text-gray-400 mb-8 text-sm md:text-base">
            Search for issues, topics, or articles related to your business account.
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <input 
              type="text" 
              placeholder="Search e.g. 'Return Policy', 'Invoice'..." 
              className="w-full pl-12 pr-4 py-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-[#008b7a] text-gray-800"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>
        </div>
      </section>

      {/* --- 2. POPULAR TOPICS (Grid) --- */}
      <section className="max-w-6xl mx-auto px-6 -mt-10 relative z-10 pb-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
           {/* Card Items */}
           {[
             { label: "Orders", icon: Package },
             { label: "Returns", icon: RefreshCw },
             { label: "Payments", icon: CreditCard },
             { label: "Account", icon: User },
             { label: "Shipping", icon: Truck },
             { label: "Disputes", icon: ShieldAlert },
           ].map((item, idx) => (
             <div key={idx} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-[#008b7a] hover:shadow-md transition-all cursor-pointer text-center group">
                <div className="w-10 h-10 mx-auto bg-gray-50 rounded-full flex items-center justify-center text-gray-500 group-hover:bg-[#008b7a] group-hover:text-white transition-colors mb-3">
                  <item.icon size={20} />
                </div>
                <h3 className="text-sm font-bold text-gray-700 group-hover:text-[#008b7a]">{item.label}</h3>
             </div>
           ))}
        </div>
      </section>

      {/* --- 3. FAQ SECTION (Accordion) --- */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <h2 className="text-2xl font-bold text-[#1e293b] mb-8">Frequently Asked Questions</h2>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <button 
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
              >
                <span className={`font-semibold ${openIndex === index ? 'text-[#008b7a]' : 'text-gray-700'}`}>
                  {faq.question}
                </span>
                {openIndex === index ? (
                  <ChevronUp size={20} className="text-[#008b7a]" />
                ) : (
                  <ChevronDown size={20} className="text-gray-400" />
                )}
              </button>
              
              {/* Answer Dropdown */}
              <div 
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openIndex === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-5 pt-0 text-sm text-gray-600 leading-relaxed border-t border-gray-100 mt-2">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- 4. STILL NEED HELP? (Footer Contact) --- */}
      <section className="bg-white border-t border-gray-200 py-16">
         <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-2">Still need help?</h2>
            <p className="text-gray-500 mb-10">Our support team is available Mon-Sat, 9am to 7pm.</p>
            
            <div className="grid md:grid-cols-3 gap-8">
               <div className="p-6 rounded-lg border border-gray-100 hover:shadow-lg transition-shadow">
                  <Phone className="mx-auto text-[#008b7a] mb-4" size={28} />
                  <h4 className="font-bold text-lg mb-1">Call Us</h4>
                  <p className="text-sm text-gray-500">+91 9412220200</p>
               </div>
               <div className="p-6 rounded-lg border border-gray-100 hover:shadow-lg transition-shadow">
                  <Mail className="mx-auto text-[#008b7a] mb-4" size={28} />
                  <h4 className="font-bold text-lg mb-1">Email Us</h4>
                  <p className="text-sm text-gray-500">info@tradyfrog.com</p>
               </div>
               <div className="p-6 rounded-lg border border-gray-100 hover:shadow-lg transition-shadow">
                  <MessageSquare className="mx-auto text-[#008b7a] mb-4" size={28} />
                  <h4 className="font-bold text-lg mb-1">Chat Support</h4>
                  <p className="text-sm text-gray-500">Available on Dashboard</p>
               </div>
            </div>
         </div>
      </section>

    </div>
  );
};

export default HelpCenter;